import { css } from '@emotion/react'
import { contentful } from 'api/contentful/api'
import { IPartnerLanding } from 'api/contentful/contentful.types'
import { postPromocodeCheck } from 'api/goodtrust/promo'
import { HelpCarousel } from 'components/content/helpCarousel/HelpCarousel'
import { PartnerAction } from 'components/content/partners/partnerAction/PartnerAction'
import { PartnerBenefits } from 'components/content/partners/partnerBenefits/PartnerBenefits'
import { PartnerHero } from 'components/content/partners/partnerHero/PartnerHero'
import { SignUpComponent } from 'components/content/partners/PartnerSignUp'
import { ProtectTheMemory } from 'components/content/protectTheMemory/ProtectTheMemory'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Layout } from 'components/layout/Layout'
import { PageTitle } from 'components/PageTitle'
import { Title_h3_default } from 'components/Typography'
import { GetStaticProps } from 'next'
import Router from 'next/router'
import { useEffect } from 'react'
import { mutateAnalyticsData } from 'utils/analytics'
import { REVALIDATE_SECS } from 'utils/constants'
import { useRunOnceReady } from 'utils/hooks'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { tryStorePromoCode } from 'utils/promo'
import { BREAKPOINTS } from 'utils/styled'
import { ApiType } from 'utils/types'

export default function Page({
  partnerLanding,
  promocode,
}: {
  partnerLanding?: IPartnerLanding
  promocode?: ApiType['PromoCodeResponse']
}) {
  useEffect(() => {
    mutateAnalyticsData({
      page: 'landing_page',
      type: 'partner',
      partner: promocode?.promo_code_info?.partner?.title,
    })
  }, [promocode?.promo_code_info?.partner?.title])

  useRunOnceReady(!!promocode?.promo_code_info?.active, () => {
    tryStorePromoCode(promocode?.promo_code_info?.code)
  })

  const actionRedirect = '/signup'

  return (
    <Layout
      header={<TopNavigationLandingPage variant="general-info" />}
      footer={<Footer variant="estate-planning" />}
    >
      <PageTitle title="GoodTrust | The Digital Legacy Platform" hideDefaultSuffix />

      <PartnerHero partnerLandingCms={partnerLanding} promocode={promocode}>
        <SignUpComponent partnerLandingCms={partnerLanding} actionRedirect={actionRedirect} />
      </PartnerHero>

      <HelpCarousel
        css={css`
          padding: 7rem 0 6rem;
          @media ${BREAKPOINTS.MD.max} {
            padding: 3rem 0 5rem;
          }
        `}
      />

      <ProtectTheMemory titleVariant={Title_h3_default} />

      <PartnerBenefits
        promocode={promocode}
        partnerLandingCms={partnerLanding}
        css={css`
          padding: 8rem 0 5rem;
          @media ${BREAKPOINTS.MD.max} {
            padding: 11rem 0 8rem;
          }
        `}
      >
        <SignUpComponent partnerLandingCms={partnerLanding} actionRedirect={actionRedirect} />
      </PartnerBenefits>

      <PartnerAction
        partnerLandingCms={partnerLanding}
        css={css`
          padding-bottom: 8rem;
          @media ${BREAKPOINTS.MD.max} {
            padding-bottom: 3rem;
          }
        `}
        onAction={() => Router.push(actionRedirect)}
      />
    </Layout>
  )
}

export function getStaticPaths() {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const promocode = ctx.params?.partnerCode as string
  const promo = await postPromocodeCheck(promocode)

  let partnerLanding
  if (promo?.json?.promo_code_info?.code) {
    partnerLanding = await contentful.getPartnerLanding({
      key: promo?.json?.promo_code_info.code,
    })
  }

  return {
    props: {
      ...(await getIntlProps(ctx)),
      partnerLanding: partnerLanding?.partnerLandingCollection?.items?.[0],
      promocode: promo?.json,
    },
    revalidate: REVALIDATE_SECS,
  }
}
